import React, { useContext, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  FaDollarSign,
  FaCheckCircle,
  FaCalendarAlt,
  FaHistory,
  FaBuilding,
} from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';
import analyticsApi from '../../../services/analyticsApi';
import {
  StatCard,
  ChartCard,
  lineOptions,
  PlanBadge,
  EmptyState,
  CompanyScopeBanner,
} from './analyticsShared';

export default function SubscriptionAnalytics() {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await analyticsApi.getMySubscription();
        if (mounted) setData(res.analytics);
      } catch {
        /* interceptor toasts */
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading subscription analytics…</div>;
  }

  if (!data || data.history.length === 0) {
    return (
      <EmptyState
        icon={<FaDollarSign />}
        title="No subscription history yet"
        message="Once you subscribe to a plan, your billing history and spend will appear here."
      />
    );
  }

  const { current, totalSpend, history } = data;
  const isCompany = user?.userType === 'company';

  // Spend over time, oldest first, using each history entry's start month.
  const sorted = [...history].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  const spendOverTime = {
    labels: sorted.map((h) =>
      h.startDate ? new Date(h.startDate).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '—'
    ),
    datasets: [
      {
        data: sorted.map((h) => h.amount || 0),
        borderColor: '#1e5c33',
        backgroundColor: 'rgba(30,92,51,0.12)',
        borderWidth: 2,
        tension: 0.35,
        fill: true,
        pointRadius: 3,
      },
    ],
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-primary">My Subscription &amp; Spend</h1>
        <p className="text-sm text-gray-500">Your subscription status and billing history</p>
      </div>

      {isCompany && <CompanyScopeBanner companyName={user?.companyName} />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Current Plan"
          value={current?.plan ? <PlanBadge plan={current.plan} active={current.active} /> : '—'}
          icon={isCompany ? <FaBuilding /> : <FaCheckCircle />}
        />
        <StatCard
          label="Status"
          value={current?.active ? 'Active' : 'Inactive'}
          icon={<FaCheckCircle />}
          accent={current?.active ? 'text-primary' : 'text-gray-400'}
        />
        <StatCard
          label="Renews / Expires"
          value={current?.endDate ? new Date(current.endDate).toLocaleDateString('en-GB') : '—'}
          icon={<FaCalendarAlt />}
          accent="text-blue-600"
        />
        <StatCard
          label="Total Spend"
          value={`$${totalSpend.toLocaleString()}`}
          icon={<FaDollarSign />}
          accent="text-primary"
        />
      </div>

      <ChartCard title="Spend Over Time">
        <div style={{ height: 240 }}>
          <Line data={spendOverTime} options={lineOptions} />
        </div>
      </ChartCard>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <FaHistory className="text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Billing History</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Plan</th>
                <th className="text-left px-4 py-2 font-medium">Start</th>
                <th className="text-left px-4 py-2 font-medium">End</th>
                <th className="text-left px-4 py-2 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted
                .slice()
                .reverse()
                .map((h, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2"><PlanBadge plan={h.plan} active={false} /></td>
                    <td className="px-4 py-2 text-gray-700">
                      {h.startDate ? new Date(h.startDate).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {h.endDate ? new Date(h.endDate).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="px-4 py-2 font-semibold text-gray-800">
                      ${(h.amount || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
